import Script from 'next/script';
import { useEffect, useState } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import { getLastSearchedHotelPlaceName } from 'shared/search/common/lastSearch';
import { trackVioCarouselImpression } from 'shared/search/common/tracking';
import { safeFormatISODate } from 'shared/util/datetime-fns';
import { ErrorBoundary } from 'shared/util/ErrorBoundary';
import { pickRandomPlace } from './popularPlaces';

const VIO_ORIGIN = 'https://partners.vio.com';

const buildWidgetSrc = (partnerKey: string, placeName: string, startDate?: Date, endDate?: Date): string => {
    const params = new URLSearchParams({
        partnerKey,
        placeName,
        hideDividers: 'true',
        currency: 'NOK',
        userCountry: 'NO',
        language: 'no',
        title: `Siste hotelltilbud i ${placeName}`,
    });

    const checkIn = safeFormatISODate(startDate);
    const checkOut = safeFormatISODate(endDate);

    if (checkIn && checkOut) {
        params.append('checkIn', checkIn);
        params.append('checkOut', checkOut);
    }

    return `${VIO_ORIGIN}/widgets/multipleHotelsWidget/index.html?${params.toString()}`;
};

const isNoOffersMessage = (data: unknown): boolean => {
    if (typeof data === 'string') {
        return data.toLowerCase().includes('nooffers') || data.toLowerCase().includes('no_offers');
    }
    if (data && typeof data === 'object') {
        const type = (data as { type?: unknown }).type;
        if (typeof type === 'string') {
            return type.toLowerCase().includes('nooffers') || type.toLowerCase().includes('no_offers');
        }
    }
    return false;
};

export const VioHotelCarousel = () => {
    const { hotelVioPartnerKey } = useConfig();
    const [placeName, setPlaceName] = useState<string | null>(null);
    const [hasOffers, setHasOffers] = useState(true);
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);

    useEffect(() => {
        const lastSearchedHotel = getLastSearchedHotelPlaceName();
        setStartDate(lastSearchedHotel?.startDate ?? undefined);
        setEndDate(lastSearchedHotel?.endDate ?? undefined);
        setPlaceName(lastSearchedHotel?.placeName ?? pickRandomPlace());
    }, []);

    useEffect(() => {
        if (!placeName) {
            return;
        }
        trackVioCarouselImpression(placeName);
    }, [placeName]);

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (event.origin !== VIO_ORIGIN) {
                return;
            }
            if (isNoOffersMessage(event.data)) {
                setHasOffers(false);
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    if (!hotelVioPartnerKey || !placeName || !hasOffers) {
        return null;
    }

    return (
        <ErrorBoundary>
            <section className="overflow-hidden mt-16 px-16 sm:px-0" data-testid="vioHotelCarousel">
                <Script
                    id="vio-set-iframe-height"
                    src={`${VIO_ORIGIN}/js/set-iframe-height/setIframeHeight.min.js`}
                    crossOrigin=""
                    strategy="afterInteractive"
                />
                <iframe
                    title={placeName}
                    src={buildWidgetSrc(hotelVioPartnerKey, placeName, startDate, endDate)}
                    className="w-full min-h-[544px] mt-[-1px] mb-[-1px]"
                />
            </section>
        </ErrorBoundary>
    );
};
